import { fn0, fn2 } from '../../../core/formatter/formatNumber';
import { getCO2Scope3Auswahl } from '../../../bereiche/dateneingabe/functions/getCO2Scope3Auswahl';
import { getCO2Scope3StructureAllgemein, getCO2Scope3StructureNachgelagert, getCO2Scope3StructureVorgelagert } from '../../../bereiche/dateneingabe/functions/getCO2Scope3Structure';
import { IDs } from '../../../helpers/IDs';

import { StructureService } from '../../../services/structure_service';
import * as Moment from 'moment';

let BEREICH_KEY: string;
let DATA: any;
let MODUL: string;

export function processThg(data: any, bereichKey: string): void {
    DATA = data || {};
    BEREICH_KEY = bereichKey || null;
    MODUL = DATA.MODUL_SELECTED || null;
    [...(data[IDs.ENT_STANDORT] || []), { tid: 'all' }].forEach((standort: any) => processThgStandort(standort.tid));
}

function filterData(data: any[], jahr?: number, standort?: string): any[] {
    return (data || [])
        .filter((item: any) => (!item.jahr || item.jahr === jahr) && (!standort || item.standort_tid_orig === standort)) || [];
}

function findData(data: any[], jahr?: number, standort?: string): any {
    return (data || []).find((item: any) => (!item.jahr || item.jahr === jahr) && (!standort || item.standort_tid_orig === standort)) || {};
}

function getSectionData(key: string, jahr: number, standort?: string): any {
    const thg: any = ((DATA[IDs.KENNZIFFERN_BERECHNET] || {})[jahr] || {}).thg || {};

    return standort ? (thg[standort] || {})[key] || {} : thg;
}

function getStructure(entity: string): any {
    return (((StructureService || {}).structures || {})[IDs.MOD_ETOOL] || {})[entity] || {};
}

function processCommonPlaceholders(jahr: number, standort: string): void {
    const placeholder: any = DATA[IDs.KENNZIFFERN_BERECHNET][jahr].thg[standort].placeholder || {};
    const unternehmen: string = ((DATA[IDs.ENT_UNTERNEHMENSDATEN] || [])[0] || {}).name
        || (DATA.RES_MAIN_LOAD_UNTERNEHMEN_SEL || {}).bezeichnung || null;

    if (unternehmen) {
        placeholder.unternehmen = unternehmen;
    }
    placeholder.datum = Moment().format('DD.MM.YYYY');
    placeholder['etool-link'] = 'www.e-tool.de';
    placeholder.jahr = jahr;
    placeholder['wri-link'] = 'www.wri.org';
}

function processFlags(jahr: number, standort: string): void {
    const flag: any = DATA[IDs.KENNZIFFERN_BERECHNET][jahr].thg[standort].flag || {};
    const systemgrenzen: string = findData(DATA[IDs.ENT_CFM_MOTIVATION], jahr).systemgrenzen;
    flag.hasScope3 = ((DATA[IDs.ENT_KONFIGURATION] || [])[0] || {}).co2_fussabdruck_orig === 1;
    flag.hasSystemgrenzen = typeof systemgrenzen !== 'undefined' && systemgrenzen !== null;
    flag.hasTochtergesellschaften = ((DATA[IDs.ENT_UNTERNEHMENSDATEN] || [])[0] || {}).hat_tochtergesellschaften_orig === 1;
}

function processMotivationZielsetzung(standort: string, jahr: number): void {
    const section: any = getSectionData('motivation_zielsetzung', jahr, standort);
    section.motivation = findData(DATA[IDs.ENT_CFM_MOTIVATION], jahr).motivation;
}

function processKonsolidierungsansatzTochtergesellschaften(standort: string, jahr: number): void {
    const section: any = getSectionData('konsolidierungsansatz_tochtergesellschaften', jahr, standort);
    section.konsolidierungsansatz = transformValue(
        'konsolidierungsansatz', findData(DATA[IDs.ENT_CFM_MOTIVATION], jahr), getStructure(IDs.ENT_CFM_MOTIVATION)
    );
    section.tochtergesellschaften = filterData(DATA[IDs.ENT_UNTERNEHMENSDATEN_TOCHTERGESELLSCHAFTEN]);
}

function processSystemgrenzenTreibhausgasbilanz(standort: string, jahr: number): void {
    const erfassungsmodus: string = ((DATA[IDs.ENT_KONFIGURATION] || [])[0] || {}).co2_fussabdruck_modus || null;
    const section: any = getSectionData('systemgrenzen_treibhausgasbilanz', jahr, standort);
    section.systemgrenzen = findData(DATA[IDs.ENT_CFM_MOTIVATION], jahr).systemgrenzen;

    if (standort === 'all') {
        section.scope3 = {};

        Object.keys(getSectionData('systemgrenzen_treibhausgasbilanz', jahr)).forEach((standortTid: string) => {
            if (standortTid === 'all') {
                return;
            }
            section.scope3[standortTid] = getSectionData('systemgrenzen_treibhausgasbilanz', jahr, standortTid).scope3 || [];
        });
        return;
    }
    section.scope3 = [];
    const scope3Items: string[] = filterData(DATA[IDs.ENT_CO2_SCOPE3_AUSWAHL], jahr, standort);
    const scope3Selected: string[] = getCO2Scope3Auswahl(DATA, { tid: +standort }, jahr, jahr);
    const scope3Structures: any[] = [
        ...getCO2Scope3StructureVorgelagert(BEREICH_KEY, MODUL) || [],
        ...getCO2Scope3StructureNachgelagert(BEREICH_KEY, MODUL) || [],
        ...getCO2Scope3StructureAllgemein() || []
    ];

    scope3Structures.forEach((structure: any) => {
        if (scope3Selected.indexOf(structure.key) === -1 && (structure.modus || erfassungsmodus) === erfassungsmodus) {
            const item: any = scope3Items.find((item: any) => structure.key === item.name) || {};
            section.scope3.push({ name: structure.name, hinweis: item.hinweis });
        }
    });
}

function processThgJahr(standort: string, jahr: number): void {
    if (!(DATA[IDs.KENNZIFFERN_BERECHNET] || {})[jahr]) {
        return;
    }

    if (!DATA[IDs.KENNZIFFERN_BERECHNET][jahr].thg) {
        DATA[IDs.KENNZIFFERN_BERECHNET][jahr].thg = {};
    }

    if (!DATA[IDs.KENNZIFFERN_BERECHNET][jahr].thg[standort]) {
        DATA[IDs.KENNZIFFERN_BERECHNET][jahr].thg[standort] = {
            flag: {},
            placeholder: {},
            konsolidierungsansatz_tochtergesellschaften: {},
            motivation_zielsetzung: {},
            systemgrenzen_treibhausgasbilanz: {},
            unternehmen_taetigkeitsprofil: {},
            umwelt_klimazertifizierungen: {}
        };
        processCommonPlaceholders(jahr, standort);
        processFlags(jahr, standort);
        processKonsolidierungsansatzTochtergesellschaften(standort, jahr);
        processMotivationZielsetzung(standort, jahr);
        processSystemgrenzenTreibhausgasbilanz(standort, jahr);
        processUnternehmenTaetigkeitsprofil(standort, jahr);
        processUmweltKlimazertifizierungen(standort, jahr);
    }
}

function processThgStandort(standort: string): void {
    // if (MODUL !== 'cfm') {
    //     return;
    // }
    let jahr: number = ((DATA[IDs.ENT_KONFIGURATION] || [])[0] || {}).startjahr_datenerfassung || null;
    const jahrMax: number = new Date().getFullYear();
    const jahrMin: number = jahr > 1900 ? (jahr > jahrMax ? +jahrMax : +jahr) : 2010;
    jahr = jahrMin;

    for (; jahr <= jahrMax; ++jahr) {
        processThgJahr(standort, jahr);
    }
}

function processUnternehmenTaetigkeitsprofil(standort: string, jahr: number): void {
    const section: any = getSectionData('unternehmen_taetigkeitsprofil', jahr, standort);
    section.vorstellung = findData(DATA[IDs.ENT_CFM_MOTIVATION], jahr).vorstellung;
}

function processUmweltKlimazertifizierungen(standort: string, jahr: number): void {
    const section: any = getSectionData('umwelt_klimazertifizierungen', jahr, standort);
    section.umweltzertifizierungen = findData(DATA[IDs.ENT_CFM_MOTIVATION], jahr).umweltzertifizierungen;
}

function transformValue(key: string, item: any, structure: any): any {
    const atts: any[] = structure.ATTS || [];
    const field: any = atts.find((item: any) => item.KEY === key) || {};
    const opts: any = field.OPTS || null;
    let value: any = item[key];

    if (Array.isArray(opts)) {
        value = (opts.find((opt: any) => opt.VALUE == item[key]) || {}).TITEL || '';
    }

    return value;
}
